import { blinkConstants } from '@thoughtspot/blink-constants';
import { getUpdatedAxisRangeBasedOnTicks } from '@thoughtspot/formatter-service';
import { create } from '@thoughtspot/logger';
import { isCustomLabelProcessingDisabled } from '@thoughtspot/session-service';
import chroma from 'chroma-js';
import _ from 'lodash';
import { getChartType, getShowStackedLabels, isYAxisStackedAsPercent, } from '../core/chart-model-utils';
import { postProcessHighchartForApplyOpacity } from '../core/point-selection-service/util';
import { getEnvFlags } from '../env';
import { offGridline, onGridline } from '../theme/chart-theme-service';
import { ChartType } from '../types';
import { shouldDisableScrollbar, shouldShowDataLabels, updateScrollConfig, } from './blink-highchart-config-util';
import { getScrollEnabledMinMaxConfig } from './highchart-config.util';
import blinkHighChartUtil from './blink-highchart-util';
import { updateDataLabels } from './data-label.util';
import { getHighchartsObject } from './highchart-init';
import { postProcessHighchartDataLabels, postProcessHighchartXAxisLabels, } from './highcharts-label-processor-service';
const Highcharts = getHighchartsObject();
export class BlinkHighchart extends Highcharts.Chart {
    constructor(config, renderComplete) {
        super(config);
        this.chartRenderComplete = (_, secondaryRenderComplete) => null;
        this.chartOptions = {};
        this._originalAxisState = new Map();
        this._didUpdateDataLabels = false;
        this.logger = create('blink-highchart');
        this._blinkSerieGetter = config._blinkSerieGetter;
        this.cachedXAxisHtml = config.cachedXAxisHtml;
        this.cachedYAxisHtml = config.cachedYAxisHtml;
        this.dataLabelsEnabledByDefault = config.dataLabelsEnabledByDefault;
        this.dataLabelsAlwaysEnabled = config.dataLabelsAlwaysEnabled;
        this.shouldShowDataLabels = config.shouldShowDataLabels;
        this.chartRenderComplete = renderComplete;
        this._init();
    }
    hasInternalChartError() {
        return false;
    }
    setScaledFonts(shouldScale) {
        this.chartOptions.shouldScaleFonts = shouldScale;
    }
    isDataLabelsEnabled() {
        return !!this.series.find(serie => !!_.get(serie, 'options.dataLabels.enabled'));
    }
    isStackLabelsEnabled() {
        return !!_.get(this, 'options.yAxis.0.stackLabels.enabled');
    }
    getSeries() {
        return this.series;
    }
    getXAxisExtremes() {
        return this.xAxis.map(axis => axis.getExtremes());
    }
    getYAxisExtremes() {
        return this.yAxis.map(axis => axis.getExtremes());
    }
    getAxisesExtremes() {
        function mappingFunction(axisExtremes) {
            return {
                min: axisExtremes.min,
                max: axisExtremes.max,
            };
        }
        const xAxisExtremes = this.getXAxisExtremes().map(mappingFunction);
        const yAxisExtremes = this.getYAxisExtremes().map(mappingFunction);
        return {
            xAxisExtremes,
            yAxisExtremes,
        };
    }
    getPlotSizeX() {
        return this.plotSizeX;
    }
    getPlotSizeY() {
        return this.plotSizeY;
    }
    setDatalabelVisibilityInUserOptions(visibility, columnId) {
        this.userOptions.chart.props.allColumns[columnId].showDataLabels = visibility;
    }
    setMarkerVisibility(markerVisible) {
        this.series.forEach(serie => {
            if (!serie.userOptions.isRegressionLine && serie.data.length > 1) {
                serie.update({
                    marker: {
                        enabled: markerVisible,
                    },
                    allowPointSelect: true,
                }, false);
            }
        });
    }
    hasStackLabels() {
        return !!this._shouldShowDataLabels;
    }
    didUpdateDataLabels() {
        return this._didUpdateDataLabels;
    }
    setStackLabelVisibility(stackLabelVisible) {
        if (!this.options) {
            return;
        }
        this._shouldShowDataLabels = stackLabelVisible;
        const options = this.options;
        this.yAxis.forEach((axis, index) => {
            blinkHighChartUtil.setYAxisStackLabels(options.yAxis[index], !!stackLabelVisible);
            axis.update({
                stackLabels: {
                    enabled: !!stackLabelVisible,
                },
            }, false);
        });
    }
    supportsDownload() {
        return !!this.getSVG;
    }
    supportsFullScreenMode() {
        return false;
    }
    secondaryRender(chartModel, dataModel) {
        this._didUpdateDataLabels = false;
        if (!this.shouldShowDataLabels && getChartType(chartModel) !== ChartType.Treemap) {
            this.chartRenderComplete(null, true);
            this.redraw();
            return;
        }
        if (getShowStackedLabels(chartModel)) {
            this.setStackLabelVisibility(true);
        }
        if (isYAxisStackedAsPercent(chartModel)) {
            this.setStackLabelVisibility(false);
        }
        updateDataLabels(this, chartModel, dataModel, null);
        this._didUpdateDataLabels = true;
        this.chartRenderComplete(null, true);
        this.redraw();
    }
    setAxisRange(axisIdx, range, alignTicks) {
        if (this.axes[axisIdx] === undefined) {
            return;
        }
        const props = this.userOptions.chart.props;
        let updatedRange = range;
        if (!(_.isNil(range === null || range === void 0 ? void 0 : range.max) && _.isNil(range === null || range === void 0 ? void 0 : range.min))) {
            const id = this.axes[axisIdx].userOptions.id;
            const highchartAxis = this.axes[axisIdx].chart.axes[axisIdx];
            updatedRange = getUpdatedAxisRangeBasedOnTicks(id, props, range, highchartAxis);
            props.axes[id].yAxisRange = updatedRange;
        }
        this.updateExtremes(this.axes[axisIdx], updatedRange);
        this.options.chart.alignTicks = alignTicks;
    }
    toggleXAxis(enabled) {
        this.xAxis.forEach((axis, idx) => this.toggleAxis(axis, enabled));
    }
    toggleYAxis(enabled) {
        this.yAxis.forEach((axis, idx) => this.toggleAxis(axis, enabled));
    }
    toggleColorAxis(enabled) {
        this.colorAxis[0].update({ visible: enabled }, false);
    }
    setSeriesVisibility(serie, visible) {
        const highchartSerie = this.get(serie.name);
        if (!highchartSerie) {
            this.addSerieToHighChart(serie.name, visible);
        }
        else {
            highchartSerie.setVisible(!!visible, false);
        }
    }
    setVisibleSeriesVisibility(visibleSeries) {
        _.forEach(this.series, serie => {
            if (visibleSeries.has(serie.name) ||
                visibleSeries.has(serie.userOptions.blinkSeriesId)) {
                this.setSeriesVisibility(serie, true);
            }
            else {
                this.setSeriesVisibility(serie, false);
            }
        });
    }
    onLegendClick(event, chart) {
        const serie = event.target;
        serie.userOptions.visible = !serie.userOptions.visible;
    }
    setExtremes(xAxisExtremes, yAxisExtremes) {
        const xAxes = this.xAxis;
        const yAxes = this.yAxis;
        if (xAxisExtremes && xAxisExtremes.length === xAxes.length) {
            xAxes.forEach((xAxis, index) => {
                const { min, max } = xAxisExtremes[index];
                if (min === xAxis.min && max === xAxis.max) {
                    return;
                }
                this.updateExtremes(xAxis, xAxisExtremes[index]);
            });
        }
        if (yAxisExtremes) {
            if (yAxes.length === 1 && yAxisExtremes.length > 1) {
                const maxMax = yAxisExtremes.max('max').max;
                const minMin = yAxisExtremes.min('min').min;
                const currentYExtreme = yAxes[0].getExtremes();
                if (currentYExtreme.max < minMin ||
                    currentYExtreme.min > maxMax) {
                    return;
                }
                this.updateExtremes(yAxes[0], { min: minMin, max: maxMax });
            }
            else if (yAxes.length > 1 && yAxisExtremes.length === 1) {
                yAxes.forEach(yAxis => this.updateExtremes(yAxis, yAxisExtremes[0]));
            }
            else {
                yAxes.forEach((yAxis, index) => this.updateExtremes(yAxis, yAxisExtremes[index]));
            }
        }
    }
    updateLegendDisplay(isVertical, legendEnabled) {
        if (this.userOptions.chart.type === 'heatmap' ||
            this.userOptions.chart.type === 'treemap') {
            this.toggleColorAxis(legendEnabled);
            this.legend.update({ enabled: legendEnabled }, false);
            return;
        }
        if (!legendEnabled) {
            this.legend.update({ enabled: false }, false);
            return;
        }
        if (!isVertical) {
            this.legend.update({
                enabled: legendEnabled,
                verticalAlign: 'bottom',
                layout: 'horizontal',
                align: 'center',
                y: blinkConstants.legendTopSpacing.HORIZONTAL,
            }, false);
        }
        else {
            this.legend.update({
                enabled: legendEnabled,
                verticalAlign: 'middle',
                layout: 'vertical',
                align: 'right',
                y: blinkConstants.legendTopSpacing.VERTICAL,
            }, false);
        }
    }
    correctYAxisRange() {
        if (this.userOptions.isYAxisShared && this.series.length > 0) {
            const visibleSeries = this.series.filter(s => !!s.visible);
            if (visibleSeries.length === 0) {
                return;
            }
            const extent = visibleSeries.reduce((currentExtent, serie) => {
                if (serie.dataMax > currentExtent.max) {
                    currentExtent.max = serie.dataMax;
                }
                if (serie.dataMin < 0 && currentExtent.min) {
                    currentExtent.min = serie.dataMin;
                }
                return currentExtent;
            }, { max: 0, min: Number.MAX_VALUE });
            if (extent.min > 0) {
                extent.min = 0;
            }
            this.yAxis[0].setExtremes(extent.min, extent.max, true, true);
        }
    }
    enableXAxisGridLines(enabled) {
        this.xAxis.forEach(axis => this.updateAxisGridLine(axis, enabled), false);
    }
    enableYAxisGridLines(enabled) {
        this.yAxis.forEach(axis => this.updateAxisGridLine(axis, enabled), false);
    }
    resetPointer() {
        this.pointer.reset();
        this.pointer.init(this, this.options);
    }
    switchToZoomMode() {
        const ZOOM_TYPE = 'xy';
        this.options.chart.panning = false;
        this.options.chart.zoomType = ZOOM_TYPE;
    }
    switchToPanMode(resetPointer) {
        this.options.chart.panning = true;
        this.options.chart.zoomType = '';
        if (!resetPointer) {
            return;
        }
        this.resetPointer();
    }
    isInPanMode() {
        if (!this.options) {
            return false;
        }
        return this.options.chart.panning;
    }
    redraw(animate = false) {
        super.redraw(animate);
        if (!isCustomLabelProcessingDisabled()) {
            postProcessHighchartDataLabels(this);
            postProcessHighchartXAxisLabels(this);
        }
        if (getEnvFlags().isFlipTooltipToContextMenuEnabled) {
            postProcessHighchartForApplyOpacity(this);
        }
    }
    lightRedraw(animate = false) {
        this.update({});
        this.redraw(animate);
    }
    ultraLightRedraw(animate = false, applyOpacity = false) {
        super.redraw(animate);
        if (getEnvFlags().isFlipTooltipToContextMenuEnabled) {
            if (applyOpacity) {
                postProcessHighchartForApplyOpacity(this);
            }
        }
    }
    toggleAxises(shouldShowXAxisLabels = true, shouldShowYAxisLabels = true) {
        if (this.userOptions.chart.type !== 'treemap') {
            this.toggleYAxis(shouldShowYAxisLabels);
            this.toggleXAxis(shouldShowXAxisLabels);
        }
    }
    updateSerieColor(serieName, color) {
        const serie = this.get(serieName);
        if (serie) {
            const colors = {
                color,
            };
            if (this.userOptions.chart.type === 'waterfall') {
                colors.color = color;
                colors.negativeColor = chroma(color)
                    .alpha(0.3)
                    .css();
            }
            this.updateSerie(serieName, colors);
        }
    }
    updateXaxisScrollConfig(series, chartType, width, height, chartModel, dataModel) {
        const minMaxConfig = getScrollEnabledMinMaxConfig(series, chartType, width, height, chartModel);
        this.setExtremes([minMaxConfig], null);
        const scrollbarConfig = {
            scrollbar: {
                enabled: true,
                showFull: true,
            },
        };
        const xAxes = this.xAxis;
        if (shouldDisableScrollbar(chartModel, series, minMaxConfig, dataModel)) {
            scrollbarConfig.scrollbar = {
                enabled: false,
                showFull: false,
            };
            updateScrollConfig(scrollbarConfig, true, xAxes);
        }
        else {
            updateScrollConfig(scrollbarConfig, false, xAxes);
        }
    }
    onDestroy() {
        this.destroy();
    }
    updateSerie(serieName, changes) {
        const serie = this.get(serieName);
        if (serie) {
            serie.update(changes);
        }
    }
    toggleAxis(axis, enabled) {
        var _a;
        const axisId = (_a = axis.userOptions) === null || _a === void 0 ? void 0 : _a.id;
        const originalState = axisId
            ? this._originalAxisState.get(axisId)
            : undefined;
        const customOverride = originalState === null || originalState === void 0 ? void 0 : originalState.labelsEnabled;
        const enabledState = _.isNil(customOverride) ? enabled : customOverride;
        if (enabledState === axis.userOptions.labels.enabled)
            return;
        axis.update({
            labels: {
                enabled: _.isNil(customOverride) ? enabled : customOverride,
            },
        }, false);
    }
    updateExtremes(axis, extremes) {
        if (!extremes) {
            return;
        }
        const { dataMin, dataMax, min, max } = axis;
        const shouldStartOnTick = _.isNil(extremes.min) && dataMin > min;
        const shouldEndOnTick = _.isNil(extremes.max) && dataMax < max;
        axis.options.startOnTick = shouldStartOnTick;
        axis.options.endOnTick = shouldEndOnTick;
        if (!shouldStartOnTick || !shouldEndOnTick) {
            axis.update({
                startOnTick: shouldStartOnTick,
                endOnTick: shouldEndOnTick,
            }, false);
        }
        try {
            axis.setExtremes(extremes.min, extremes.max, true);
        }
        catch (e) {
            this.logger.warn(e);
        }
    }
    _init() {
        this.pointer.init(this, this.options);
        this._cacheOriginalAxisState();
    }
    _cacheOriginalAxisState() {
        [...this.xAxis, ...this.yAxis].forEach(axis => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
            const axisId = (_a = axis.userOptions) === null || _a === void 0 ? void 0 : _a.id;
            if (!axisId)
                return;
            this._originalAxisState.set(axisId, {
                labelsEnabled: (_c = (_b = axis.userOptions) === null || _b === void 0 ? void 0 : _b.labels) === null || _c === void 0 ? void 0 : _c.enabled,
                lineWidth: (_e = (_d = axis.options) === null || _d === void 0 ? void 0 : _d.lineWidth) !== null && _e !== void 0 ? _e : 0,
                tickWidth: (_g = (_f = axis.options) === null || _f === void 0 ? void 0 : _f.tickWidth) !== null && _g !== void 0 ? _g : 0,
                minorTickWidth: (_j = (_h = axis.options) === null || _h === void 0 ? void 0 : _h.minorTickWidth) !== null && _j !== void 0 ? _j : 0,
                titleText: (_l = (_k = axis.options) === null || _k === void 0 ? void 0 : _k.title) === null || _l === void 0 ? void 0 : _l.text,
            });
        });
    }
    getOriginalAxisState(axisId) {
        return this._originalAxisState.get(axisId);
    }
    setDataLabelVisibilityBySeriesId(visibility, seriesIds) {
        const dataLabelsVisibility = shouldShowDataLabels(this.series, seriesIds, visibility);
        this.series.forEach(serie => {
            if (seriesIds.includes(serie.userOptions.blinkSeriesId)) {
                this.setDatalabelVisibilityInSerie(dataLabelsVisibility, serie);
            }
        });
    }
    setDatalabelVisibilityInSerie(visibility, serie) {
        serie.update({
            dataLabels: {
                enabled: visibility,
            },
        }, false);
    }
    addSerieToHighChart(serieName, isVisible) {
        const vizModelSerie = this._blinkSerieGetter(serieName);
        if (!vizModelSerie) {
            this.logger.warn('ThoughtSpot is not able to display the series ', serieName);
            return;
        }
        vizModelSerie.id = vizModelSerie.name;
        vizModelSerie.visible = isVisible;
        this.addSeries(vizModelSerie, true);
    }
    getGridLineConfig(axis, enabled) {
        const userOptions = axis.userOptions;
        const enabledWidth = _.isNil(userOptions.gridLineCustomWidth)
            ? onGridline.gridLineWidth
            : userOptions.gridLineCustomWidth;
        return {
            ...axis.userOptions,
            gridLineWidth: enabled ? enabledWidth : offGridline.gridLineWidth,
        };
    }
    updateAxisGridLine(axis, enabled) {
        const gridLineConfig = this.getGridLineConfig(axis, enabled);
        if (axis.userOptions.gridLineWidth === gridLineConfig.gridLineWidth)
            return;
        axis.update(gridLineConfig, false);
    }
}
//# sourceMappingURL=blink-highchart.js.map